/* eslint-disable react/prop-types */
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../../lib/api";
import { EntityWizard } from "../../components/shell";
import SearchableSelect from "../../components/SearchableSelect";
import VoiceTextarea from "../../components/VoiceTextarea";
import DateTimePicker from "../../components/DateTimePicker";

/**
 * JobCardWizard — Phase 7 Sub-iter 7A.
 *
 * Multi-step inline creation flow for new Job Cards.  Replaces the
 * `JobCardDetail.jsx isCreate` branch — Trust Contract Rule 2 compliant
 * (NEVER modal).  Auto-saves the draft to localStorage so a closed tab
 * doesn't lose work.
 *
 * Steps:
 *   1. Who    : Customer + Site
 *   2. What   : Title + Description (voice-enabled)
 *   3. When   : Scheduled start + end
 *   4. Review : Summary card + Create button
 *
 * Submit hits `POST /api/jobcards` and redirects to the JC detail page
 * (in non-create mode) so editing continues with the canonical editor.
 *
 * Iter 413 (Dirk, 2026-02-28) — Templates. When invoked with
 * `?template_id=X`, the wizard fetches the template, pre-fills:
 *   • customer_id (if the template is customer-scoped)
 *   • title       (from the template name)
 *   • tasks       (checklist scaffold — reset transient fields)
 * The user still completes site / tech / staff / vehicle / dates / etc.
 * per normal flow. After successful create we stamp usage on the
 * template so the Templates screen can rank most-used.
 */

const POST_NEW_JC = async (form) => {
  const payload = {
    customer_id: form.customer_id,
    site_id: form.site_id,
    title: form.title?.trim(),
    description: form.description || "",
    scheduled_at: form.scheduled_at || null,
    scheduled_end: form.scheduled_end || null,
    phase: "picking",
  };
  // Iter 413 — checklist scaffold from the picked template. Reset
  // transient fields so nothing leaks from the template author's copy.
  if (Array.isArray(form._templateTasks) && form._templateTasks.length) {
    payload.checklist = form._templateTasks.map((t) => ({
      ...t,
      id: (crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`),
      status: "pending",
      comment: "",
      comments: [],
      photos: [],
      assignee_ids: [],
      assignee_names: [],
      assignee_edit_by_id: null,
      assignee_edit_by_name: null,
      assignee_edit_at: null,
    }));
  }
  const { data } = await api.post("/jobcards", payload);
  return data;
};

function WhoStep({ form, patch, errors }) {
  const [customers, setCustomers] = useState([]);
  const [sites, setSites] = useState([]);

  useEffect(() => {
    api.get("/customers")
      .then(({ data }) => setCustomers(Array.isArray(data) ? data : (data?.items || [])))
      .catch(() => setCustomers([]));
  }, []);

  useEffect(() => {
    if (!form.customer_id) { setSites([]); return; }
    api.get(`/sites?customer_id=${form.customer_id}`)
      .then(({ data }) => setSites(Array.isArray(data) ? data : []))
      .catch(() => setSites([]));
  }, [form.customer_id]);

  return (
    <div className="space-y-4" data-testid="jc-wizard-step-who">
      <div>
        <label className="text-[10px] uppercase tracking-wider text-[#9aa3b5]">Customer *</label>
        <SearchableSelect
          value={form.customer_id || ""}
          onChange={(cid) => patch({ customer_id: cid, site_id: "" })}
          options={customers.map((c) => ({
            id: c.id,
            label: c.name,
            sublabel: [c.external_ref && `Acc# ${c.external_ref}`, c.phone].filter(Boolean).join(" · "),
            searchTerms: [c.name, c.external_ref, c.phone, c.email].filter(Boolean),
          }))}
          placeholder="— pick a customer —"
          testId="jc-wizard-customer"
          required
        />
        {errors.customer_id && (
          <p className="text-xs text-rose-300 mt-1">{errors.customer_id}</p>
        )}
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-wider text-[#9aa3b5]">Site *</label>
        <SearchableSelect
          value={form.site_id || ""}
          onChange={(sid) => patch({ site_id: sid })}
          options={sites.map((s) => ({
            id: s.id,
            label: s.name || s.address || "—",
            sublabel: s.address,
            searchTerms: [s.name, s.address].filter(Boolean),
          }))}
          placeholder={form.customer_id ? "— pick a site —" : "Pick a customer first"}
          testId="jc-wizard-site"
          disabled={!form.customer_id}
        />
        {errors.site_id && (
          <p className="text-xs text-rose-300 mt-1">{errors.site_id}</p>
        )}
      </div>
    </div>
  );
}

function WhatStep({ form, patch, setField, errors }) {
  const hasTemplate = Array.isArray(form._templateTasks) && form._templateTasks.length > 0;
  return (
    <div className="space-y-4" data-testid="jc-wizard-step-what">
      {hasTemplate && (
        <div className="hv-card p-2.5 border border-emerald-500/30 bg-emerald-500/5 text-[11px] text-emerald-200"
             data-testid="jc-wizard-template-hint">
          <span className="font-bold">Template loaded:</span> {form._templateName} · {form._templateTasks.length} task
          {form._templateTasks.length === 1 ? "" : "s"} will be pre-filled.
          Give this specific job card its own descriptive title below (the template name is only for finding the scaffold).
        </div>
      )}
      <div>
        <label className="text-[10px] uppercase tracking-wider text-[#9aa3b5]">Title *</label>
        <input
          type="text"
          value={form.title || ""}
          onChange={(e) => setField("title", e.target.value)}
          placeholder={hasTemplate
            ? "e.g. Call-out at ABC · pump replacement (this specific job)"
            : "e.g. Replace booster pump and pressure tank"}
          className="hv-input w-full"
          data-testid="jc-wizard-title"
        />
        {errors.title && (
          <p className="text-xs text-rose-300 mt-1">{errors.title}</p>
        )}
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-wider text-[#9aa3b5]">Description</label>
        <VoiceTextarea
          value={form.description || ""}
          onChange={(v) => patch({ description: v })}
          placeholder="What's the problem, what needs doing? Or tap the mic."
          rows={5}
          context="lead_activity"
          testIdPrefix="jc-wizard-desc"
        />
      </div>
    </div>
  );
}

function WhenStep({ form, setField }) {
  return (
    <div className="space-y-4" data-testid="jc-wizard-step-when">
      <div>
        <label className="text-[10px] uppercase tracking-wider text-[#9aa3b5]">Scheduled start</label>
        <DateTimePicker
          value={form.scheduled_at || ""}
          onChange={(v) => setField("scheduled_at", v)}
          testId="jc-wizard-scheduled-at"
        />
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-wider text-[#9aa3b5]">Scheduled end</label>
        <DateTimePicker
          value={form.scheduled_end || ""}
          onChange={(v) => setField("scheduled_end", v)}
          testId="jc-wizard-scheduled-end"
        />
      </div>
      <p className="text-xs text-[#647087]">
        Leave blank to schedule later from the job card detail page.
      </p>
    </div>
  );
}

function ReviewStep({ form }) {
  return (
    <div className="space-y-3 text-sm" data-testid="jc-wizard-step-review">
      <div className="hv-card p-4 space-y-2">
        <div>
          <span className="text-[#647087] text-xs uppercase tracking-wider">Title:</span>{" "}
          <span className="text-white">{form.title || "—"}</span>
        </div>
        <div>
          <span className="text-[#647087] text-xs uppercase tracking-wider">Customer:</span>{" "}
          <span className="text-white font-mono text-xs">{form.customer_id || "—"}</span>
        </div>
        <div>
          <span className="text-[#647087] text-xs uppercase tracking-wider">Site:</span>{" "}
          <span className="text-white font-mono text-xs">{form.site_id || "—"}</span>
        </div>
        {form.scheduled_at && (
          <div>
            <span className="text-[#647087] text-xs uppercase tracking-wider">Scheduled:</span>{" "}
            <span className="text-white">{form.scheduled_at}</span>
            {form.scheduled_end && <> → {form.scheduled_end}</>}
          </div>
        )}
        {form.description && (
          <div>
            <span className="text-[#647087] text-xs uppercase tracking-wider">Description:</span>
            <p className="text-[#a0aabf] whitespace-pre-wrap mt-1">{form.description}</p>
          </div>
        )}
        {Array.isArray(form._templateTasks) && form._templateTasks.length > 0 && (
          <div data-testid="jc-wizard-template-summary">
            <span className="text-[#647087] text-xs uppercase tracking-wider">
              From template:
            </span>{" "}
            <span className="text-emerald-300 text-xs">
              {form._templateName || "—"} · {form._templateTasks.length} task{form._templateTasks.length === 1 ? "" : "s"} pre-filled
            </span>
            <ul className="mt-2 text-xs text-[#a0aabf] space-y-0.5 list-disc list-inside">
              {form._templateTasks.slice(0, 8).map((t) => (
                <li key={t.id}>{t.label}</li>
              ))}
              {form._templateTasks.length > 8 && (
                <li className="text-[#647087] list-none">
                  + {form._templateTasks.length - 8} more…
                </li>
              )}
            </ul>
          </div>
        )}
      </div>
      <p className="text-xs text-[#647087]">
        Tap <strong>Create Job Card</strong> below to save. You&apos;ll be taken to the JC detail
        page where you can add tasks, photos, and assignees.
      </p>
    </div>
  );
}

const validateWho = (form) => {
  const errors = {};
  if (!form.customer_id) errors.customer_id = "Pick a customer.";
  if (!form.site_id) errors.site_id = "Pick a site.";
  return { ok: Object.keys(errors).length === 0, errors };
};

const validateWhat = (form) => {
  const errors = {};
  if (!form.title || !form.title.trim()) errors.title = "Title is required.";
  return { ok: Object.keys(errors).length === 0, errors };
};

export default function JobCardWizard() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const templateId = params.get("template_id") || "";
  const customerIdParam = params.get("customer_id") || "";
  // Iter 420 — deep-link from a Project's Linked tab. When URL is
  // /jobcards/new?project_id=xxx we stamp project_id on the created
  // job card so it auto-links back to the project on save.
  const projectIdParam = params.get("project_id") || "";
  const [tmpl, setTmpl] = useState(null);
  const [ready, setReady] = useState(!templateId);

  // Iter 413 — Load the picked template once so the initialForm can
  // pre-fill customer/title/tasks. We hold the wizard render until the
  // template resolves so the autosave key doesn't lock in blank values.
  useEffect(() => {
    if (!templateId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/jobcard-templates/${templateId}`);
        if (!cancelled) setTmpl(data);
      } catch { /* silent — user can still finish flow */ }
      if (!cancelled) setReady(true);
    })();
    return () => { cancelled = true; };
  }, [templateId]);

  const handleSubmit = async (form) => {
    const payload = projectIdParam ? { ...form, project_id: projectIdParam } : form;
    const created = await POST_NEW_JC(payload);
    if (created?.id) {
      // Iter 420 — if we came from a project via ?project_id, also
      // POST the reverse link (project → jc) so the project's Linked
      // tab sees it instantly. POST_NEW_JC already sends project_id
      // as part of the create payload, but doing this explicitly
      // guarantees the back-reference regardless of jobcards.py
      // implementation details.
      if (projectIdParam) {
        try {
          await api.post(`/projects/${projectIdParam}/link`, {
            kind: "jobcard",
            id: created.id,
          });
        } catch { /* silent — jc still created */ }
      }
      // Iter 413 — bump usage count so the Templates screen can rank
      // most-used. Non-blocking on failure.
      if (templateId) {
        try { await api.post(`/jobcard-templates/${templateId}/stamp-usage`); }
        catch { /* silent */ }
      }
      navigate(`/jobcards/${created.id}`);
    }
  };

  if (!ready) {
    return (
      <div className="hv-card p-6 text-center text-sm text-[#a0aabf]"
           data-testid="jc-wizard-loading-template">
        Loading template…
      </div>
    );
  }

  return (
    <EntityWizard
      title={tmpl ? `New Job Card · from "${tmpl.name}"` : "New Job Card"}
      steps={[
        { key: "who", label: "Who", render: WhoStep, validate: validateWho },
        { key: "what", label: "What", render: WhatStep, validate: validateWhat },
        { key: "when", label: "When", render: WhenStep },
        { key: "review", label: "Review", render: ReviewStep },
      ]}
      initialForm={{
        customer_id: tmpl?.customer_id || customerIdParam || "",
        site_id: "",
        // Iter 413.1 (Dirk, 2026-02-28) — When a template is used, the
        // JC title MUST be filled in by the creator (job-specific
        // name), NOT copied from the template. Templates are just
        // reusable task scaffolds — the JC identity comes from the
        // user. Leaving the title blank forces the wizard's "What"
        // step validation to prompt for it.
        title: "",
        description: "",
        scheduled_at: "",
        scheduled_end: "",
        _templateTasks: tmpl?.tasks || [],
        _templateName: tmpl?.name || "",
      }}
      autosaveKey={templateId ? `jc:new:tpl:${templateId}` : "jc:new"}
      onSubmit={handleSubmit}
      onCancelTo="/jobcards"
      submitLabel="Create Job Card"
    />
  );
}
